import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { track } from '@/lib/analytics';
import { Footer, MarketingHeader, SEO } from '@/components';
import {
  FiCheckCircle,
  FiClipboard,
  FiFileText,
  FiBriefcase,
  FiUserPlus,
} from 'react-icons/fi';

declare global {
  interface Window {
    gtagSendEvent: (eventName: string) => void;
    fbq: (...args: unknown[]) => void;
  }
}

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

const AUDIENCE = 'lawyers' as const;

const handleProSignUpClick = (location: string) => {
  track('Click Sign Up', { location, audience: AUDIENCE });
  if (typeof window.gtagSendEvent === 'function') {
    window.gtagSendEvent('click_sign_up');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Lead');
  }
};

const ARTIFACTS = [
  {
    icon: FiClipboard,
    title: 'Parenting Time Compliance Report',
    body: 'Logged exchanges, adherence stats, and provenance—usable whether one or both parents are on Harbor.',
  },
  {
    icon: FiFileText,
    title: 'Expense Dispute Summary',
    body: 'Disputed expenses, balances, and receipt context in one counsel-facing summary.',
  },
  {
    icon: FiBriefcase,
    title: 'Hearing Prep Brief',
    body: 'Curated packet: compliance, disputes, and a chronological timeline for hearing week.',
  },
] as const;

function LearnLawyersPage() {
  useEffect(() => {
    track('Learn Lawyers Screen', { audience: AUDIENCE });
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Harbor for Counsel | Solo or Paired Co-Parenting Records'
        description='One-page overview for family lawyers: Harbor works solo or paired. Parenting Time Compliance Report, Expense Dispute Summary, Hearing Prep Brief, and how pro invite codes work.'
        path='/learn/lawyers'
      />
      <MarketingHeader />

      <main className='pt-28 pb-16'>
        <div className='max-w-3xl mx-auto px-6'>
          <p
            className='text-primary font-semibold text-sm uppercase tracking-wide mb-3'
            style={{ ...nunito, fontWeight: 600 }}
          >
            For counsel
          </p>
          <h1
            className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'
            style={{ ...nunito, fontWeight: 700 }}
          >
            When the other parent won&apos;t join, your client still gets a
            record you can use.
          </h1>
          <p
            className='text-lg text-gray-600 mb-3 leading-relaxed'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Harbor is a co-parenting app that works <strong>solo or paired</strong>.
            Clients document parenting time, expenses, and messages. You receive
            prep artifacts—not a requirement that both parents adopt the same
            platform first.
          </p>
          <p
            className='text-base text-gray-500 mb-10 leading-relaxed'
            style={{ ...nunito, fontWeight: 400 }}
          >
            One parent can start alone. If the other joins later, records continue
            in a shared household. Reports label solo vs paired provenance so
            counsel knows what was logged by whom.
          </p>

          <section className='mb-12'>
            <h2
              className='text-xl font-bold text-gray-900 mb-5'
              style={{ ...nunito, fontWeight: 700 }}
            >
              Artifacts for counsel
            </h2>
            <ul className='space-y-5'>
              {ARTIFACTS.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.title} className='flex gap-4'>
                    <div className='w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0'>
                      <Icon className='w-5 h-5 text-primary' />
                    </div>
                    <div>
                      <h3
                        className='font-bold text-gray-900 mb-1'
                        style={{ ...nunito, fontWeight: 700 }}
                      >
                        {item.title}
                      </h3>
                      <p
                        className='text-gray-600 text-sm leading-relaxed'
                        style={{ ...nunito, fontWeight: 400 }}
                      >
                        {item.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className='mb-12 border-t border-gray-200 pt-10'>
            <h2
              className='text-xl font-bold text-gray-900 mb-5'
              style={{ ...nunito, fontWeight: 700 }}
            >
              How invite codes work
            </h2>
            <ol className='space-y-4 mb-8'>
              {[
                'Create a free Harbor pro account (no co-parent invite needed).',
                'Copy an invite code or link from your pro dashboard.',
                'Client redeems the code, starts a trial, and can share exports with you.',
                'Download Compliance, Dispute Summary, or Hearing Prep Brief when shared.',
              ].map((step, index) => (
                <li key={step} className='flex items-start gap-3'>
                  <FiCheckCircle className='w-5 h-5 text-primary flex-shrink-0 mt-0.5' />
                  <span
                    className='text-gray-700'
                    style={{ ...nunito, fontWeight: 400 }}
                  >
                    <span className='font-semibold text-gray-900'>
                      {index + 1}.
                    </span>{' '}
                    {step}
                  </span>
                </li>
              ))}
            </ol>

            <div className='flex flex-col sm:flex-row gap-3'>
              <Link
                to='/auth/pro/sign-up'
                onClick={() => handleProSignUpClick('Learn Lawyers')}
                className='inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors'
                style={{ ...nunito, fontWeight: 600 }}
              >
                <FiUserPlus className='w-5 h-5' />
                Create free pro account
              </Link>
              <Link
                to='/learn/practitioners'
                className='inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold py-3 px-6 rounded-lg transition-colors'
                style={{ ...nunito, fontWeight: 600 }}
              >
                Full practitioners overview
              </Link>
            </div>
          </section>

          <p
            className='text-xs text-gray-500 border-t border-gray-100 pt-6'
            style={{ ...nunito, fontWeight: 400 }}
          >
            harborcoparent.com · Client plans from $9.99/month or $49.99/year after
            a 7-day trial. Court-order language templates are available separately when
            parties later want platform language in an order—not a prerequisite to
            start documenting.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/learn/lawyers')({
  component: LearnLawyersPage,
});
